import React, { PropsWithChildren, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Whisper, Popover } from 'rsuite';
import SubMenuItem, { SubMenuItemProps } from './SubMenuItem';
import Icon from '@/common/view/icons';
import Text from '@/common/view/Text';
import colors from '@/common/view/colors';
import { useDelayedVisibility } from './useDelayedVisibility';

const Li = styled.li``;

const Item = styled.div<{ $active: 'true' | 'false'; $collapsed?: boolean }>`
  display: flex;
  height: 48px;
  padding: 12px 16px 12px 19.2px;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  transition: padding 250ms ease-in-out;
  svg {
    margin-right: ${({ $collapsed }) => ($collapsed ? '0' : '6.4px')};
    flex-shrink: 0;
    transition: margin-right 250ms ease-in-out;
    path:not([fill="none"]) {
      fill: ${({ $active }) => ($active === 'true' ? colors.blue50 : colors.gray40)};
    }
    path[fill="none"] {
      fill: none;
      stroke: ${({ $active }) => ($active === 'true' ? colors.blue50 : colors.gray40)};
    }
  }
  &:hover {
    cursor: pointer;
    background: ${colors.blue10};
  }
`;

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  span {
    font-size: 12.8px;
    line-height: 17.6px;
  }
`;

const SubMenuWrapper = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? '300px' : '0')};
  transition: max-height 250ms ease-in-out;
`;

const PopoverMenu = styled.div`
  min-width: 100px;
`;

const ExpandIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => {
  return expanded ? (
    <Icon.ArrowTop width={14.4} height={14.4} />
  ) : (
    <Icon.ArrowDown width={14.4} height={14.4} />
  );
};

interface NavItemProps extends PropsWithChildren {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  title: string;
  href: string;
  collapsed?: boolean;
}

const MenuItem: React.FC<NavItemProps> = ({ Icon, title, href, children, collapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const showLabel = useDelayedVisibility(collapsed);

  useEffect(() => {
    setActive(location.pathname.includes(href));
    setExpanded(location.pathname.includes(href));
  }, [location.pathname, href]);

  const handleClick = () => {
    if (children) {
      if (!collapsed) setExpanded(!expanded);
    } else {
      navigate(href);
    }
  };

  const itemContent = (
    <Item $active={active ? 'true' : 'false'} $collapsed={collapsed} onClick={handleClick}>
      <TitleBox>
        {Icon && <Icon width={17.6} height={17.6} />}
        {showLabel && (
          <Text color={active ? 'blue50' : 'gray40'} typo="body3">
            {title}
          </Text>
        )}
      </TitleBox>
      {showLabel && children && <ExpandIcon expanded={expanded} />}
    </Item>
  );

  const popoverContent = children ? (
    <Popover>
      <PopoverMenu>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<SubMenuItemProps>, { inPopover: true });
          }
          return child;
        })}
      </PopoverMenu>
    </Popover>
  ) : (
    <Popover>
      <PopoverMenu>
        <SubMenuItem title={title} href={href} inPopover />
      </PopoverMenu>
    </Popover>
  );

  const wrappedContent = collapsed ? (
    <Whisper placement="rightStart" trigger="hover" speaker={popoverContent} enterable>
      {itemContent}
    </Whisper>
  ) : (
    itemContent
  );

  return (
    <Li>
      {wrappedContent}
      {children && (
        <SubMenuWrapper $visible={!collapsed && expanded}>
          {children}
        </SubMenuWrapper>
      )}
    </Li>
  );
};

export default MenuItem;
